import React from 'react';
import { Phone, AlertTriangle, CheckCircle, FileText, Users, Scale } from 'lucide-react';
export function CdlDoINeedOnePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1673">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1674">
        <div className="container mx-auto px-4" data-id="element-1675">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1676">
            Commercial Driver License
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-1677">
            Complete CDL Requirements Guide
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1678">
        <div className="max-w-4xl mx-auto" data-id="element-1679">
          {/* Who Needs a CDL */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1680">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1681">
              <FileText className="mr-3 text-[#B1050B]" size={32} data-id="element-1682" />
              Who Needs a CDL?
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1683">
              Any resident who intends to operate:
            </p>

            <div className="space-y-6" data-id="element-1684">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1685">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1686">VEHICLES:</h3>
                <p className="text-gray-700" data-id="element-1687">
                  • Having a gross vehicle weight rating (GVWR) of{' '}
                  <strong data-id="element-1688">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-1689">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1690">
                  COMBINATION VEHICLES:
                </h3>
                <p className="text-gray-700" data-id="element-1691">
                  • Towing a trailer or other vehicles with a GVWR of{' '}
                  <strong data-id="element-1692">10,001 pounds or more</strong> with a gross
                  combination weight rating of{' '}
                  <strong data-id="element-1693">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-1694">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1695">ANY VEHICLES:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-1696">
                  <li data-id="element-1697">
                    • Designed to transport <strong data-id="element-1698">16 or more people</strong>{' '}
                    (including the driver)
                  </li>
                  <li data-id="element-1699">
                    • Carrying <strong data-id="element-1700">hazardous materials</strong> in amounts
                    requiring placards
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-1701">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-1702">
                Important Definitions:
              </h3>
              <div className="space-y-3 text-gray-700" data-id="element-1703">
                <p data-id="element-1704">
                  <strong data-id="element-1705">Gross Vehicle Weight Rating (GVWR)</strong> is the
                  recommended maximum total weight of the vehicle and load as
                  designated by the vehicle manufacturer. The GVWR label is
                  usually found on the driver side door post of the power unit
                  and on or near the front of the trailer.
                </p>
                <p data-id="element-1706">
                  <strong data-id="element-1707">Gross Combination Weight Rating (GCWR)</strong> means
                  the value specified by the manufacturer as the maximum loaded
                  weight of a combination vehicle. In the absence of a label,
                  the GCWR will be calculated by adding the GVWR of the power
                  unit to the GVWR of the vehicle(s) or trailer(s) being towed.
                </p>
              </div>
            </div>
          </div>

          {/* Age Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1708">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1709">
              How Old Must I Be To Obtain a CDL?
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-1710">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-1711">
                <p className="text-3xl font-bold text-blue-600 mb-2" data-id="element-1712">21</p>
                <p className="font-bold text-gray-900 mb-2" data-id="element-1713">
                  Interstate Commerce
                </p>
                <p className="text-gray-700" data-id="element-1714">
                  Drive a commercial motor vehicle across state lines
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-1715">
                <p className="text-3xl font-bold text-green-600 mb-2" data-id="element-1716">18</p>
                <p className="font-bold text-gray-900 mb-2" data-id="element-1717">
                  Intrastate Commerce (Michigan only)
                </p>
                <p className="text-gray-700" data-id="element-1718">
                  Drive within Michigan (except hazardous materials requiring
                  placards - must be 21)
                </p>
              </div>
            </div>
          </div>

          {/* Driving Record Eligibility */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-1719">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-1720">
              <AlertTriangle className="mr-3" size={28} data-id="element-1721" />
              Driving Record Eligibility Requirements
            </h2>

            <p className="text-gray-800 mb-4" data-id="element-1722">
              Before a person may apply for an original CDL, they must qualify
              based on their driver record. Any of the following will{' '}
              <strong data-id="element-1723">disqualify</strong> a person from applying:
            </p>

            <ul className="space-y-3 text-gray-800" data-id="element-1724">
              <li className="flex items-start" data-id="element-1725">
                <span className="mr-2" data-id="element-1726">•</span>
                <span data-id="element-1727">
                  The inability to make required certifications on the CDL
                  Certification Form (BFS-103)
                </span>
              </li>
              <li className="flex items-start" data-id="element-1728">
                <span className="mr-2" data-id="element-1729">•</span>
                <span data-id="element-1730">Having a license from more than one state</span>
              </li>
              <li className="flex items-start" data-id="element-1731">
                <span className="mr-2" data-id="element-1732">•</span>
                <span data-id="element-1733">
                  An active Michigan or out-state suspension, revocation,
                  denial, or cancellation of driving privilege
                </span>
              </li>
              <li className="flex items-start" data-id="element-1734">
                <span className="mr-2" data-id="element-1735">•</span>
                <span data-id="element-1736">
                  A suspension or revocation in the 36 months immediately
                  preceding application
                </span>
              </li>
              <li className="flex items-start" data-id="element-1737">
                <span className="mr-2" data-id="element-1738">•</span>
                <span data-id="element-1739">
                  Conviction of any six-point violation in the 24 months
                  immediately preceding application
                </span>
              </li>
              <li className="flex items-start" data-id="element-1740">
                <span className="mr-2" data-id="element-1741">•</span>
                <span data-id="element-1742">
                  Conviction for operating a commercial motor vehicle while
                  impaired in the 24 months immediately preceding application
                </span>
              </li>
            </ul>

            <div className="bg-yellow-50 rounded-lg p-4 mt-4" data-id="element-1743">
              <p className="text-sm text-gray-800" data-id="element-1744">
                <strong data-id="element-1745">NOTE:</strong> Suspensions for Failure to Appear in
                Court (FAC), Failure to Comply with a Court Judgment (FCJ),
                failure to appear for a Driver Assessment re-examination,
                financial responsibility, non-sufficient funds checks, and
                suspension/revocation for temporary medical condition will NOT
                disqualify an applicant.
              </p>
            </div>
          </div>

          {/* CDL Groups */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1746">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1747">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-1748" />
              What Type of CDL Do I Need?
            </h2>

            <div className="space-y-6" data-id="element-1749">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1750">
                <h3 className="text-2xl font-bold text-blue-600 mb-3" data-id="element-1751">
                  GROUP A
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-1752">
                  Any combination of vehicles with a gross combination weight
                  rating (GCWR) of <strong data-id="element-1753">26,001 pounds or more</strong>,
                  provided the gross vehicle weight rating (GVWR) of the
                  vehicle(s) being towed is{' '}
                  <strong data-id="element-1754">10,001 pounds or more</strong>.
                </p>
                <p className="text-sm text-gray-600 italic" data-id="element-1755">
                  A Group A endorsement will allow a driver to operate a Group B
                  or Group C vehicle provided they possess required
                  endorsement(s).
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-1756">
                <h3 className="text-2xl font-bold text-green-600 mb-3" data-id="element-1757">
                  GROUP B
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-1758">
                  Any single vehicle with a gross vehicle weight rating (GVWR)
                  of <strong data-id="element-1759">26,001 pounds or more</strong>, or any such vehicle
                  towing a vehicle with a GVWR of{' '}
                  <strong data-id="element-1760">10,000 pounds or less</strong>.
                </p>
                <p className="text-sm text-gray-600 italic" data-id="element-1761">
                  A Group B endorsement will allow a driver to operate a Group C
                  vehicle provided they possess required endorsement(s).
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-1762">
                <h3 className="text-2xl font-bold text-yellow-600 mb-3" data-id="element-1763">
                  GROUP C
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-1764">
                  Any single vehicle with a GVWR of{' '}
                  <strong data-id="element-1765">26,001 pounds or less</strong>, or combination of
                  vehicles where the GVWR of the vehicle being towed is{' '}
                  <strong data-id="element-1766">10,000 pounds or less</strong> designed to:
                </p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-1767">
                  <li data-id="element-1768">○ Transport 16 or more persons (including the driver)</li>
                  <li data-id="element-1769">
                    ○ Transport hazardous materials in amounts requiring
                    placards
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Endorsements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1770">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1771">
              What Endorsements Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-1772">
              In addition to the appropriate CDL vehicle group designation,
              endorsements are required to operate the following:
            </p>

            <div className="space-y-4" data-id="element-1773">
              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1774">
                <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1775">
                  T
                </span>
                <div data-id="element-1776">
                  <p className="font-bold text-gray-900" data-id="element-1777">
                    DOUBLE or TRIPLE TRAILERS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1778">
                    (Triple trailer combinations are not permitted in Michigan)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1779">
                <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1780">
                  P
                </span>
                <div data-id="element-1781">
                  <p className="font-bold text-gray-900" data-id="element-1782">PASSENGER</p>
                  <p className="text-sm text-gray-600" data-id="element-1783">
                    For vehicles designed to carry 16 or more people (including
                    the driver)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1784">
                <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1785">
                  N
                </span>
                <div data-id="element-1786">
                  <p className="font-bold text-gray-900" data-id="element-1787">TANK</p>
                  <p className="text-sm text-gray-600" data-id="element-1788">
                    For vehicles designed to haul liquids/liquefied gases in
                    permanently mounted cargo tanks rated at 119 gallons or
                    more, or portable cargo tanks rated at 1,000 gallons or more
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1789">
                <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1790">
                  H
                </span>
                <div data-id="element-1791">
                  <p className="font-bold text-gray-900" data-id="element-1792">HAZARDOUS MATERIALS</p>
                  <p className="text-sm text-gray-600" data-id="element-1793">
                    To carry hazardous materials in amounts requiring placards
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1794">
                <span className="bg-orange-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1795">
                  X
                </span>
                <div data-id="element-1796">
                  <p className="font-bold text-gray-900" data-id="element-1797">
                    Combined TANK and HAZARDOUS MATERIAL
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1798">
                    When an applicant adds both the tank and hazardous materials
                    endorsements
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1799">
                <span className="bg-yellow-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1800">
                  S
                </span>
                <div data-id="element-1801">
                  <p className="font-bold text-gray-900" data-id="element-1802">SCHOOL BUS</p>
                  <p className="text-sm text-gray-600" data-id="element-1803">
                    For commercial motor vehicles used to transport pre-primary,
                    primary, or secondary school students
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Medical Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1804">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1805">
              Driver Qualification Requirements
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-1806">
              Unless exempt, you must comply with the federal commercial driver
              qualification requirements, including the medical/physical
              qualifications found in the Federal Motor Carrier Safety
              Regulations,{' '}
              <strong data-id="element-1807">49 CFR Part 391 — Qualifications of Drivers</strong>.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6" data-id="element-1808">
              <p className="text-gray-800 mb-3" data-id="element-1809">
                When applying for your CDL, you will sign a certification form
                (BFS-103) that states you meet these requirements before
                receiving your CDL.
              </p>
              <p className="text-gray-800" data-id="element-1810">
                Before taking any CDL driver skills tests, you must provide a
                valid <strong data-id="element-1811">Medical Examiner's Certificate</strong> (commonly
                known as a DOT card) or medical waiver to your examiner.
              </p>
            </div>

            <h3 className="font-bold text-gray-900 mb-4" data-id="element-1812">
              Additional Information on Medical/Physical Requirements:
            </h3>

            <div className="space-y-3 text-gray-700" data-id="element-1813">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1814">
                <p className="font-semibold mb-1" data-id="element-1815">Intrastate (in Michigan):</p>
                <p className="text-sm" data-id="element-1816">Michigan Center for Truck Safety</p>
                <a href="http://www.truckingsafety.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-1817">
                  www.truckingsafety.org
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1818">
                <p className="font-semibold mb-1" data-id="element-1819">
                  Interstate (across state lines):
                </p>
                <p className="text-sm" data-id="element-1820">
                  Federal Motor Carrier Safety Administration
                </p>
                <p className="text-sm" data-id="element-1821">
                  315 W. Allegan St., Room 205, Lansing, Michigan 48933
                </p>
                <p className="text-sm" data-id="element-1822">Phone: 517/377-1866</p>
                <a href="http://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-1823">
                  www.fmcsa.dot.gov
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1824">
                <p className="font-semibold mb-1" data-id="element-1825">School bus requirements:</p>
                <p className="text-sm" data-id="element-1826">Michigan Department of Education</p>
                <p className="text-sm" data-id="element-1827">Office of School Support Services</p>
                <p className="text-sm" data-id="element-1828">
                  P.O. Box 30008, Lansing, Michigan 48909
                </p>
                <p className="text-sm" data-id="element-1829">Phone: 517/373-6388</p>
              </div>
            </div>
          </div>

          {/* CDL Exemptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1830">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1831">
              Are There CDL Exemptions?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-1832">
              The following people do <strong data-id="element-1833">NOT</strong> need a Commercial
              Driver License (CDL):
            </p>

            <div className="space-y-4" data-id="element-1834">
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-1835">
                <p className="font-bold text-gray-900 mb-2" data-id="element-1836">
                  ACTIVE DUTY MILITARY
                </p>
                <p className="text-gray-700" data-id="element-1837">
                  Operating military vehicles with military licenses (includes
                  National Guard)
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-1838">
                <p className="font-bold text-gray-900 mb-2" data-id="element-1839">
                  POLICE AND FIREFIGHTERS
                </p>
                <p className="text-gray-700" data-id="element-1840">
                  Operating authorized emergency vehicles
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-1841">
                <p className="font-bold text-gray-900 mb-2" data-id="element-1842">FARMERS</p>
                <p className="text-gray-700 mb-2" data-id="element-1843">
                  Operating vehicles within a 150 mile radius of their farm
                </p>
                <p className="text-sm text-gray-600" data-id="element-1844">
                  An F-endorsement is needed by farmers operating combination
                  vehicles whose towing vehicle has a GVWR of 26,001 pounds or
                  more. A knowledge test, but no skills test, is required. The
                  F-endorsement is NOT a CDL.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-1845">
                <p className="font-bold text-gray-900 mb-2" data-id="element-1846">INDIVIDUALS</p>
                <p className="text-gray-700" data-id="element-1847">
                  Operating motor homes or other vehicles used exclusively to
                  transport personal possessions or family members, for
                  non-business purposes
                </p>
              </div>
            </div>
          </div>

          {/* How to Obtain */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-1848">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1849">How Do I Obtain a CDL?</h2>

            <div className="space-y-4" data-id="element-1850">
              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1851">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1852">
                  1
                </span>
                <div data-id="element-1853">
                  <p className="font-semibold mb-2" data-id="element-1854">
                    Visit Secretary of State Branch Office
                  </p>
                  <ul className="text-sm space-y-1 text-gray-100" data-id="element-1855">
                    <li data-id="element-1856">• Show your driver license</li>
                    <li data-id="element-1857">• Show proof of Social Security number</li>
                    <li data-id="element-1858">• Fill out CDL Certification form (BFS-103)</li>
                    <li data-id="element-1859">• Meet driver eligibility requirements</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1860">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1861">
                  2
                </span>
                <div data-id="element-1862">
                  <p className="font-semibold mb-2" data-id="element-1863">Pass Required Tests</p>
                  <p className="text-sm text-gray-100" data-id="element-1864">
                    Pass required knowledge and vision tests to obtain a CDL
                    Temporary Instruction Permit (TIP)
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1865">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1866">
                  3
                </span>
                <div data-id="element-1867">
                  <p className="font-semibold mb-2" data-id="element-1868">Pay Fees</p>
                  <p className="text-sm text-gray-100" data-id="element-1869">
                    Pay your CDL Group Designation and Endorsement fees
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1870">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1871">
                  4
                </span>
                <div data-id="element-1872">
                  <p className="font-semibold mb-2" data-id="element-1873">Schedule Skills Test</p>
                  <p className="text-sm text-gray-100" data-id="element-1874">
                    Schedule and take your CDL skills test through an approved
                    third party driver skills test organization
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1875">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1876">
                  5
                </span>
                <div data-id="element-1877">
                  <p className="font-semibold mb-2" data-id="element-1878">Receive Certificate</p>
                  <p className="text-sm text-gray-100" data-id="element-1879">
                    After passing, receive Michigan Department of State Driver
                    Skills Test Certificate (TPT-010). Valid for 1 year from
                    date of issuance.
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-1880">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1881">
                  6
                </span>
                <div data-id="element-1882">
                  <p className="font-semibold mb-2" data-id="element-1883">Get Your CDL</p>
                  <p className="text-sm text-gray-100" data-id="element-1884">
                    Take skills test certificate to SOS branch office, pay
                    correction fee, and receive your photo CDL in the mail
                    within about 10 business days
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1885">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1886">
              Need Help with CDL Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-1887">
              Expert guidance on Commercial Driver License requirements and
              testing
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1888">
              <Phone className="mr-3" size={24} data-id="element-1889" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1890">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}